import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { NgIf } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';

// ─────────────────────────────────────────────────────────────────────────────
// BulkActionsBarComponent (ui-bulk-actions-bar)
// ─────────────────────────────────────────────────────────────────────────────
// Floating bar that surfaces bulk actions for the rows currently selected in a
// table. It renders the selection count, the host's own action buttons, and a
// clear-selection affordance.
//
// Companion to `ui-checkbox` / `RangeSelectionTracker`: the host feeds it the
// size of the selection those two produce, and `cleared` is expected to empty
// that same selection (which also resets the range anchor).
//
// Actions are content-projected rather than configured, so each table keeps its
// own permission directives, tooltips, disabled logic and icons. Project plain
// buttons carrying the styling hooks the bar provides:
//
//   - `.ui-bulk-action`          — borderless action, light grey hover
//   - `.ui-bulk-action--icon`    — square, icon-only; pair it with a tooltip
//   - `.ui-bulk-action--danger`  — destructive action, red text + red hover
//
// Positioning is the host's job: the bar is `position: absolute`, so give the
// table container `position: relative` and the bar centres itself against it.
// The host is also responsible for padding the scroll area so the bar never
// covers the last row.
//
// Usage:
//   <ui-bulk-actions-bar *ngIf="selected.length" [count]="selected.length" (cleared)="clearSelection()">
//     <button class="ui-bulk-action" (click)="bulkDuplicate()">…</button>
//     <button class="ui-bulk-action ui-bulk-action--danger" (click)="bulkDelete()">…</button>
//   </ui-bulk-actions-bar>
// ─────────────────────────────────────────────────────────────────────────────

@Component({
  selector: 'ui-bulk-actions-bar',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgIf, MatTooltipModule, TranslateModule],
  templateUrl: './bulk-actions-bar.component.html',
  styleUrls: ['./bulk-actions-bar.component.scss'],
})
export class BulkActionsBarComponent {
  /** Number of selected rows, shown in the leading badge. */
  @Input() count = 0;

  /** Set false to hide the clear-selection button. */
  @Input() showClear = true;

  /** Emitted when the user clears the selection. The host should empty it. */
  @Output() cleared = new EventEmitter<void>();
}
